import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import styles from './updateMockChildDialog.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { UPDATE_MOCK_CHILD } from '@/modules/API/parent/noAccountHandler.ts'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface UpdateMockChildProps {
    daycareId?: number
    childId: number
    childFirstname: string
    childBirthdate: string
    childPreRegistrationBelgium: string
    open: boolean
    handleClose: () => void
    handleRefetchChild: () => void
}

const UpdateMockChildDialog: FunctionComponent<UpdateMockChildProps> = ({
    childId,
    daycareId,
    childFirstname,
    childBirthdate,
    childPreRegistrationBelgium,
    open,
    handleClose,
    handleRefetchChild
}: UpdateMockChildProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [firstname, setFirstname] = useState<string>(childFirstname)
    const [birthdate, setBirthdate] = useState<string>(childBirthdate)
    const [preRegistrationBelgium, setPreRegistrationBelgium] = useState<string>(childPreRegistrationBelgium)

    const [updateChild, { loading: loadingUpdateChild }] = useMutation(UPDATE_MOCK_CHILD, {
        variables: {
            childId: childId,
            daycareId: daycareId,
            firstname: firstname,
            birthdate: birthdate,
            preRegistrationBelgium: preRegistrationBelgium
        },
        onCompleted: () => {
            showNotificationSuccess(t('childCreated'))

            handleRefetchChild()

            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorCreatingChild'))
        }
    })

    const disabled: boolean =
        firstname === childFirstname &&
        birthdate === childBirthdate &&
        preRegistrationBelgium === childPreRegistrationBelgium

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('modifyChild')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('firstname')}
                        value={firstname}
                        fullWidth
                        required
                        className={styles.Input}
                        setValue={setFirstname}
                        format={'pascal'}
                        type={'text'}
                    />
                </div>
                <div className={styles.InputContainer}>
                    <DatePickerComponent
                        label={t('birthdate')}
                        value={birthdate}
                        setValue={setBirthdate}
                        disablePast={false}
                        className={styles.Input}
                        allowEmpty
                    />
                </div>
                {preRegistrationBelgium && preRegistrationBelgium !== '' && (
                    <div className={styles.InputContainer}>
                        <UserInput
                            label={t('preRegistration')}
                            value={preRegistrationBelgium}
                            fullWidth
                            className={styles.Input}
                            setValue={setPreRegistrationBelgium}
                            format={'pascal'}
                            type={'text'}
                        />
                    </div>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'update'}
                        onClick={updateChild}
                        className={styles.Button}
                        disabled={loadingUpdateChild || disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default UpdateMockChildDialog
